<script setup>
// Luxury-grade call-to-action for the public marketing surface (2026-09-06):
// a pill with an uppercase, letter-spaced label, a gold hairline and an
// optional leading glyph. `tone` names the panel it sits on (dark = night
// panels and photo heroes, light = paper sections); `variant` solid is the
// primary action, line the secondary. Renders an <a> when `href` is given,
// otherwise a <button> (pass `type="submit"` for forms). Extra attrs
// (target, rel, aria-*) fall through.
import { computed } from 'vue'

const props = defineProps({
  href:     { type: String,  default: '' },
  type:     { type: String,  default: 'button' },  // <button> only
  variant:  { type: String,  default: 'solid' },   // solid | line
  tone:     { type: String,  default: 'light' },   // light | dark
  glyph:    { type: String,  default: '' },        // arrow | chat | telegram | whatsapp | line | … (unknown chat-ish names fall back to the bubble)
  block:    { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
})

// Stroke paths on a 24-grid: an arrow for "go", a paper plane for Telegram,
// a speech bubble for every other chat platform.
const GLYPHS = {
  arrow:    'M5 12h14M13 6l6 6-6 6',
  telegram: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
  chat:     'M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5z',
}
const tag  = computed(() => (props.href ? 'a' : 'button'))
const path = computed(() => {
  if (!props.glyph || props.glyph === 'none') return ''
  return GLYPHS[props.glyph] || GLYPHS.chat
})
</script>

<template>
  <component
    :is="tag"
    :href="href || undefined"
    :type="href ? undefined : type"
    :disabled="href ? undefined : disabled"
    :class="['lux', `lux--${variant}`, `lux--${tone}`, { 'lux--block': block }]"
  >
    <span v-if="$slots.icon || path" class="lux__icon">
      <slot name="icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path :d="path" /></svg></slot>
    </span>
    <span class="lux__label"><slot /></span>
  </component>
</template>

<style scoped>
.lux {
  display: inline-flex; align-items: center; justify-content: center; gap: 0.65rem;
  padding: 0.95rem 1.6rem; border-radius: 999px; text-decoration: none; cursor: pointer;
  appearance: none; -webkit-appearance: none;
  font-family: var(--font-body, var(--body)); font-size: 0.78rem; font-weight: 600;
  letter-spacing: 0.14em; text-transform: uppercase; line-height: 1; white-space: nowrap;
  border: 1px solid transparent;
  transition: transform 160ms ease, background 160ms ease, border-color 160ms ease, box-shadow 200ms ease, color 160ms ease;
}
.lux:hover:not(:disabled) { transform: translateY(-1px); }
.lux:focus-visible { outline: 2px solid var(--gold); outline-offset: 3px; }
.lux:disabled { opacity: 0.5; cursor: not-allowed; }
.lux--block { width: 100%; }
/* On a phone a long label ("Get a quote for your wall") must wrap rather than
   widen the page: nowrap made the studio page 433px wide on a 390px screen. */
@media (max-width: 30rem) {
  .lux { white-space: normal; text-align: center; line-height: 1.25; max-width: 100%; }
}
.lux__icon { display: inline-flex; width: 1.05rem; height: 1.05rem; flex: none; }
.lux__icon :deep(svg) { width: 100%; height: 100%; stroke: currentColor; fill: none; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }

/* On a night panel or a photo hero */
.lux--dark.lux--solid {
  background: var(--foam); color: var(--night); border-color: var(--foam);
  box-shadow: 0 18px 40px -22px rgba(0, 0, 0, 0.9), inset 0 0 0 1px color-mix(in oklch, var(--gold) 45%, transparent);
}
.lux--dark.lux--solid:hover:not(:disabled) { background: color-mix(in oklch, var(--gold) 28%, var(--foam)); border-color: var(--gold); }
.lux--dark.lux--line { background: color-mix(in oklch, var(--night) 22%, transparent); color: var(--foam); border-color: color-mix(in oklch, var(--gold) 65%, transparent); backdrop-filter: blur(6px); }
.lux--dark.lux--line:hover:not(:disabled) { background: color-mix(in oklch, var(--gold) 18%, transparent); border-color: var(--gold); }

/* On paper */
.lux--light.lux--solid {
  background: var(--night); color: var(--foam); border-color: var(--night);
  box-shadow: 0 16px 34px -20px color-mix(in oklch, var(--ink) 60%, transparent), inset 0 0 0 1px color-mix(in oklch, var(--gold) 40%, transparent);
}
.lux--light.lux--solid:hover:not(:disabled) { background: var(--night2); border-color: var(--night2); }
.lux--light.lux--line { background: transparent; color: var(--ink); border-color: color-mix(in oklch, var(--gold) 70%, var(--line)); }
.lux--light.lux--line:hover:not(:disabled) { background: color-mix(in oklch, var(--gold) 14%, transparent); border-color: var(--gold); }

@media (prefers-reduced-motion: reduce) { .lux { transition: none; } .lux:hover:not(:disabled) { transform: none; } }
</style>
